import React, { memo, useCallback, useEffect, useRef } from 'react'
import ReactFlow, {
  useNodesState,
  useEdgesState,
  updateEdge,
  addEdge,
  Background,
  MarkerType,
} from 'reactflow'

import 'reactflow/dist/style.css'
import Activity from '../nodes/Activity'
import EndRejectedActivity from '../nodes/EndRejectedActivity'
import EndAcceptedActivity from '../nodes/EndAcceptedActivity'
import { getRandomColor } from '../utils/transformer'
import { useActivities, useCurrentRoute, useLocalMetadata, useRoutes } from '../store'
import CustomConnectionLine from './CustomConnectionLine'
import FloatingEdge from './FloatingEdge'
import { workflowService } from '../../../../services/admin/workflow'
import { useAuthContext } from '../../../../hooks/useAuthContext'

const nodeTypes = {
  activity: Activity,
  startActivity: Activity,
  endRejectedActivity: EndRejectedActivity,
  endAcceptedActivity: EndAcceptedActivity,
}

const proOptions = { hideAttribution: true }

const defaultEdgeOptions = {
  style: { strokeWidth: 3, stroke: 'black' },
  type: 'smoothstep',
  markerEnd: {
    type: MarkerType.ArrowClosed,
    color: 'black',
  },
}

const edgeTypes = {
  floating: FloatingEdge,
}

const connectionLineStyle = {
  strokeWidth: 3,
  stroke: 'black',
}

const FlowEditor = ({ initialNodes = [], initialEdges = [], selectActivity }) => {
  const updatingEdge = useRef(false)
  const edgeUpdateSuccessful = useRef(true)
  const { user } = useAuthContext()
  const metadataRef = useRef(null)
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges)
  const activities = useActivities((state) => state.activities)
  const setActivities = useActivities((state) => state.setActivities)
  const metadata = useLocalMetadata((state) => state.metadata)
  const setMetadata = useLocalMetadata((state) => state.setMetadata)
  const setCurrentRoute = useCurrentRoute((state) => state.setCurrentRoute)
  const addRoute = useRoutes((state) => state.addRoute)
  const routes = useRoutes((state) => state.routes)
  const setRoutes = useRoutes((state) => state.setRoutes)

  /* syncing up activity workflow metadata*/
  useEffect(() => {
    if (user != null) {
      if (metadataRef.current) {
        clearTimeout(metadataRef.current)
      }
      metadataRef.current = setTimeout(() => {
        workflowService.updateActivityTemplateMetadata(user, metadata).then((res) => {
          console.log('workflowMetadataSynced', res)
        })
        metadataRef.current = null
      }, 100)
    }
  }, [metadata, user])

  useEffect(() => {
    setNodes(initialNodes)
  }, [initialNodes, setNodes])

  useEffect(() => {
    setEdges(initialEdges)
  }, [initialEdges, setEdges])

  const getRouteDetails = (params, activities) => {
    let firstActivity = null
    let secondActivity = null
    activities.forEach((it) => {
      if (Number(params.source) == it.id) {
        firstActivity = it
      } else if (Number(params.target) == it.id) {
        secondActivity = it
      }
    })
    return {
      type: secondActivity.rank > firstActivity.rank ? 'progress' : 'regress',
      sourceActivity: firstActivity,
      targetActivity: secondActivity,
    }
  }

  const onConnect = useCallback(
    (params) => {
      const detail = getRouteDetails(params, activities)
      const color = getRandomColor(detail.type)
      const edge = {
        ...params,
        id: `r-${params.source}-${params.target}`,
        type: 'smoothstep',
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 20,
          height: 20,
          color: color,
        },
        style: {
          strokeWidth: 3,
          stroke: color,
        },
        label: 'New Route',
        sourceActivity: detail.sourceActivity,
        targetActivity: detail.targetActivity,
        route: { route_type: detail.type },
      }

      const routeActivityMap = {
        from_activity_template_id: +edge.source,
        to_activity_template_id: +edge.target,
      }
      const routeTemplate = {
        route_template_name: edge.label,
        route_type: edge?.route?.route_type || 'neutral',
        user_id: user.id,
        sequence: 1,
      }

      /* connect event - create route */
      workflowService.createRoute(user, routeActivityMap, routeTemplate).then((res) => {
        // update local routes state
        const newRoute = {
          route_template_id: res?.routeTemplate?.id,
          route_template_name: edge?.label,
          route_type: detail?.type,
          map_id: res?.routeActivityMap?.id,
          from_activity_template_id: +edge.source,
          to_activity_template_id: +edge.target,
          sequence: 1,
        }

        addRoute(newRoute)

        return setEdges((eds) =>
          addEdge(
            {
              ...edge,
              route: {
                ...edge.route,
                route_template_id: newRoute,
                map_id: res?.routeActivityMap?.id,
                sequence: 1,
              },
            },
            eds
          )
        )
      })
    },
    [setEdges, activities]
  )

  const onEdgeUpdateStart = useCallback(() => {
    edgeUpdateSuccessful.current = false
  }, [])

  const onEdgeUpdate = useCallback(
    (oldEdge, newConnection) => {
      updatingEdge.current = true
      edgeUpdateSuccessful.current = true
      setEdges((els) => updateEdge(oldEdge, newConnection, els))

      // update route map - update route map
      workflowService
        .updateRouteMap(user, {
          oldRoute: { source: +oldEdge.source, target: +oldEdge.target },
          newRoute: { source: +newConnection.source, target: +newConnection.target },
        })
        .then((res) => {
          // update local route map
          const updatedRoutes = routes.map((route) => {
            return route.map_id == oldEdge.route.map_id
              ? {
                  ...route,
                  from_activity_template_id: +newConnection.source,
                  to_activity_template_id: +newConnection.target,
                }
              : route
          })
          setRoutes(updatedRoutes)
        })
    },
    [user, routes]
  )

  const onEdgeUpdateEnd = useCallback(
    (_, edge) => {
      if (!edgeUpdateSuccessful.current) {
        setEdges((eds) => eds.filter((e) => e.id !== edge.id))
      }

      if (updatingEdge.current == false) {
        /* delete event - delete route */
        workflowService.deleteRoute(user, edge?.source, edge?.target).then((res) => {
          // update local route map
          const updatedRoutes = routes.filter((route) => {
            return route.map_id != edge.route.map_id
          })
          setRoutes(updatedRoutes)
        })
      } else {
        updatingEdge.current = false
      }

      edgeUpdateSuccessful.current = true
    },
    [user, routes]
  )

  const onNodeDrag = useCallback((_, node) => {
    const data = {
      workflow_metadata: { position: node.position, height: node.height, width: node.width },
      activity_template_id: +node.id,
    }

    // check if current activity metadata is there
    const present = metadata?.find((it) => it.activity_template_id == data.activity_template_id)

    if (present) {
      const updatedNodeMetadata = metadata?.map((meta) => {
        return meta?.activity_template_id == data?.activity_template_id ? data : meta
      })

      setActivities(
        activities.map((activity) => {
          const currentMetadata = updatedNodeMetadata.find(
            (meta) => meta.activity_template_id == activity.id
          )
          return currentMetadata
            ? { ...activity, workflow_metadata: currentMetadata.workflow_metadata }
            : activity
        })
      )

      setMetadata(updatedNodeMetadata)
    } else {
      const updatedNodeMetadata = [...metadata, data]
      setActivities(
        activities.map((it) => {
          const currentMetadata = updatedNodeMetadata.find(
            (meta) => meta.activity_template_id == it.id
          )
          return currentMetadata
            ? { ...it, workflow_metadata: currentMetadata.workflow_metadata }
            : it
        })
      )
      setMetadata(updatedNodeMetadata)
    }
  })

  const getFilteredEdges = () => {
    if (!selectActivity) {
      return edges
    }

    const selectedActivity = activities.find(
      (activity) => activity.activity_template_name === selectActivity
    )

    if (!selectedActivity) {
      return edges
    }

    const finalEdges = edges.filter((edge) => {
      const sourceId = edge.source.toString()
      const targetId = edge.target.toString()
      const selectedId = selectedActivity.id.toString()
      return sourceId === selectedId || targetId === selectedId
    })

    return finalEdges
  }

  return (
    <div style={{ width: '100%', height: 'calc(100vh - 180px)' }}>
      <ReactFlow
        nodes={nodes}
        edges={getFilteredEdges()}
        onNodesChange={onNodesChange}
        onEdgeUpdate={onEdgeUpdate}
        onEdgeUpdateStart={onEdgeUpdateStart}
        onEdgeUpdateEnd={onEdgeUpdateEnd}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        proOptions={proOptions}
        edgeTypes={edgeTypes}
        defaultEdgeOptions={defaultEdgeOptions}
        selectable={true}
        connectionLineComponent={CustomConnectionLine}
        connectionLineStyle={connectionLineStyle}
        onEdgeDoubleClick={(_, edge) => setCurrentRoute(edge)}
        onNodeDrag={onNodeDrag}
      >
        <Background />
      </ReactFlow>
    </div>
  )
}

export default memo(FlowEditor)
